import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useServices } from '../hooks/useServices';
import type { ChecklistItem, ServiceChecklist, ServiceWithChecklist } from '../types';
import { useRoleAccess } from '../../users/hooks/useRoleAccess';
import { Plus, Search, Clock, DollarSign, Tag, ChevronRight, CheckSquare, Trash2, Camera } from 'lucide-react';

export default function Services() {
  const navigate = useNavigate();
  const { services, addService } = useServices();
  const permissions = useRoleAccess();
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newService, setNewService] = useState<{
    name: string;
    description: string;
    basePrice: number;
    duration: number;
    category: 'residential' | 'commercial' | 'deep-cleaning' | 'specialized';
    addOns: string[];
    checklist?: ServiceChecklist;
  }>({
    name: '',
    description: '',
    basePrice: 0,
    duration: 2,
    category: 'residential',
    addOns: [''],
    checklist: undefined
  });
  
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([]);

  const filteredServices = services.filter(service => {
    const matchesSearch = 
      service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = categoryFilter === 'all' || service.category === categoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  const handleAddService = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!newService.name.trim() || !newService.description.trim() || newService.basePrice <= 0) {
      alert('Please fill in all required fields with valid values.');
      return;
    }
    
    const filteredAddOns = newService.addOns.filter(addOn => addOn.trim() !== '');
    
    // Create checklist if items exist
    let checklist: ServiceChecklist | undefined = undefined;
    if (checklistItems.length > 0) {
      checklist = {
        id: `checklist-${Date.now()}`,
        name: `${newService.name} Checklist`,
        description: `Standard checklist for ${newService.name} service`,
        items: checklistItems,
        isActive: true
      };
    }
    
    const serviceToAdd = {
      ...newService,
      addOns: filteredAddOns,
      checklist,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    console.log('Submitting service:', serviceToAdd);
    
    addService(serviceToAdd)
      .then(() => {
        console.log('Service creation successful');
        setNewService({
          name: '',
          description: '',
          basePrice: 0,
          duration: 2,
          category: 'residential',
          addOns: [''],
          checklist: undefined
        });
        setChecklistItems([]);
        setShowAddForm(false);
      })
      .catch((error) => {
        console.error('Failed to add service:', error);
        alert(`Failed to add service: ${error.message || 'Unknown error'}. Please try again.`);
      });
  };

  const addAddOnField = () => {
    setNewService({
      ...newService,
      addOns: [...newService.addOns, '']
    });
  };

  const updateAddOn = (index: number, value: string) => {
    const updatedAddOns = [...newService.addOns];
    updatedAddOns[index] = value;
    setNewService({
      ...newService,
      addOns: updatedAddOns
    });
  };

  const removeAddOn = (index: number) => {
    if (newService.addOns.length > 1) {
      const updatedAddOns = newService.addOns.filter((_, i) => i !== index);
      setNewService({
        ...newService,
        addOns: updatedAddOns
      });
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'residential':
        return 'bg-blue-100 text-blue-800';
      case 'commercial':
        return 'bg-purple-100 text-purple-800';
      case 'deep-cleaning':
        return 'bg-green-100 text-green-800';
      case 'specialized':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const addChecklistItem = () => {
    const newItem: ChecklistItem = {
      id: `item-${Date.now()}`,
      text: '',
      completed: false,
      required: false,
      allowPhotos: false,
      order: checklistItems.length + 1,
      category: undefined
    };
    setChecklistItems([...checklistItems, newItem]);
  };

  const updateChecklistItem = (index: number, updates: Partial<ChecklistItem>) => {
    const updatedItems = [...checklistItems];
    updatedItems[index] = { ...updatedItems[index], ...updates };
    setChecklistItems(updatedItems);
  };

  const removeChecklistItem = (index: number) => {
    setChecklistItems(checklistItems.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Services</h1>
        {permissions.canEditServices && (
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>Add Service</span>
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search services..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div>
            <select
              className="block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="all">All Categories</option>
              <option value="residential">Residential</option>
              <option value="commercial">Commercial</option>
              <option value="deep-cleaning">Deep Cleaning</option>
              <option value="specialized">Specialized</option>
            </select>
          </div>
        </div>
      </div>

      {/* Add Service Modal */}
      {showAddForm && permissions.canEditServices && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Add New Service</h2>
            <form onSubmit={handleAddService} className="space-y-4">
              <div>
                <label htmlFor="serviceName" className="block text-sm font-medium text-gray-700">Service Name</label>
                <input
                  id="serviceName"
                  type="text"
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  value={newService.name}
                  onChange={(e) => setNewService({...newService, name: e.target.value})}
                />
              </div>
              <div>
                <label htmlFor="serviceDescription" className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  id="serviceDescription"
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                  value={newService.description}
                  onChange={(e) => setNewService({...newService, description: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="servicePrice" className="block text-sm font-medium text-gray-700">Base Price ($)</label>
                  <input
                    id="servicePrice"
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    value={newService.basePrice}
                    onChange={(e) => setNewService({...newService, basePrice: Number.parseFloat(e.target.value)})}
                  />
                </div>
                <div>
                  <label htmlFor="serviceDuration" className="block text-sm font-medium text-gray-700">Duration (hours)</label>
                  <input
                    id="serviceDuration"
                    type="number"
                    required
                    min="0.5"
                    step="0.5"
                    className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    value={newService.duration}
                    onChange={(e) => setNewService({...newService, duration: Number.parseFloat(e.target.value)})}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="serviceCategory" className="block text-sm font-medium text-gray-700">Category</label>
                <select
                  id="serviceCategory"
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  value={newService.category}
                  onChange={(e) => setNewService({...newService, category: e.target.value as 'residential' | 'commercial' | 'deep-cleaning' | 'specialized'})}
                >
                  <option value="residential">Residential</option>
                  <option value="commercial">Commercial</option>
                  <option value="deep-cleaning">Deep Cleaning</option>
                  <option value="specialized">Specialized</option>
                </select>
              </div>
              <div>
                <div className="block text-sm font-medium text-gray-700 mb-2">Add-Ons</div>
                {newService.addOns.map((addOn, index) => (
                  <div key={`addon-${index}`} className="flex items-center space-x-2 mt-2">
                    <input
                      type="text"
                      placeholder="Add-on service"
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      value={addOn}
                      onChange={(e) => updateAddOn(index, e.target.value)}
                    />
                    {newService.addOns.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeAddOn(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addAddOnField}
                  className="mt-2 text-indigo-600 hover:text-blue-800 text-sm"
                >
                  + Add another add-on
                </button>
              </div>
              
              {/* Job Checklist Section */}
              <div>
                <div className="block text-sm font-medium text-gray-700 mb-2">
                  Job Checklist (Optional)
                </div>
                <div className="border border-gray-300 rounded-lg p-4 space-y-3">
                  {checklistItems.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-2">
                      No checklist items added. Add items to create a quality checklist for this service.
                    </p>
                  ) : (
                    checklistItems.map((item, index) => (
                      <div key={item.id} className="border border-gray-200 rounded-lg p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Item {index + 1}</span>
                          <button
                            type="button"
                            onClick={() => removeChecklistItem(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        <input
                          type="text"
                          placeholder="Checklist item description (e.g., Clean all surfaces)"
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          value={item.text}
                          onChange={(e) => updateChecklistItem(index, { text: e.target.value })}
                        />
                        <div className="flex items-center space-x-4">
                          <label className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={item.required}
                              onChange={(e) => updateChecklistItem(index, { required: e.target.checked })}
                              className="rounded border-gray-300"
                            />
                            <span className="text-sm text-gray-600">Required</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={item.allowPhotos}
                              onChange={(e) => updateChecklistItem(index, { allowPhotos: e.target.checked })}
                              className="rounded border-gray-300"
                            />
                            <span className="text-sm text-gray-600">
                              <Camera className="h-3 w-3 inline mr-1" />
                              Photos
                            </span>
                          </label>
                        </div>
                        <input
                          type="text"
                          placeholder="Category (optional, e.g., Kitchen, Bathroom)"
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          value={item.category || ''}
                          onChange={(e) => updateChecklistItem(index, { category: e.target.value || undefined })}
                        />
                      </div>
                    ))
                  )}
                  <button
                    type="button"
                    onClick={addChecklistItem}
                    className="w-full border-2 border-dashed border-gray-300 rounded-lg py-3 text-sm text-gray-600 hover:border-indigo-300 hover:text-indigo-600 transition-colors flex items-center justify-center space-x-2"
                  >
                    <CheckSquare className="h-4 w-4" />
                    <span>Add Checklist Item</span>
                  </button>
                </div>
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Add Service
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Services Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredServices.map((service) => {
          const serviceWithChecklist = service as ServiceWithChecklist;
          return (
          <button
            key={service.id} 
            onClick={() => navigate(`/services/${service.id}`)}
            onKeyDown={(e) => e.key === 'Enter' && navigate(`/services/${service.id}`)}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md hover:border-indigo-300 transition-all cursor-pointer group text-left w-full"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors">{service.name}</h3>
                  <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-indigo-600 transition-colors" />
                </div>
                <p className="text-sm text-gray-600 mt-1">{service.description}</p>
              </div>
              <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getCategoryColor(service.category)}`}>
                {service.category.replace('-', ' ')}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="flex items-center space-x-2">
                <DollarSign className="h-4 w-4 text-green-600" />
                <div>
                  <p className="text-sm text-gray-500">Base Price</p>
                  <p className="text-lg font-semibold text-gray-900">${service.basePrice}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4 text-indigo-600" />
                <div>
                  <p className="text-sm text-gray-500">Duration</p>
                  <p className="text-lg font-semibold text-gray-900">{service.duration}h</p>
                </div>
              </div>
            </div>

            {/* Checklist Indicator */}
            {serviceWithChecklist.checklist && serviceWithChecklist.checklist.items.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center space-x-2 text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg">
                  <CheckSquare className="h-4 w-4" />
                  <span>Quality checklist ({serviceWithChecklist.checklist.items.length} items)</span>
                </div>
              </div>
            )}

            {service.addOns.length > 0 && (
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center space-x-2 mb-2">
                  <Tag className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-500">Available Add-Ons</span>
                </div>
                <div className="space-y-1">
                  {service.addOns.map((addOn, index) => (
                    <span key={`${service.id}-addon-${index}`} className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded mr-2">
                      {addOn}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </button>
        );
        })}
      </div>

      {filteredServices.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No services found matching your criteria.</p>
        </div>
      )}
    </div>
  );
}